/**
 * Checklist service API calls — thin wrappers around the HTTP client.
 *
 * Evaluation jobs are async: POST returns 202 with a job_id; callers must
 * poll GET /v1/evaluate/{job_id} until status is 'completed' | 'failed'.
 */

import { checklistApi } from './client';
import type {
  ApiResponse,
  ChecklistList,
  ChecklistDetailOut,
  ChecklistOut,
  RuleOut,
  RuleList,
  EvaluateJobCreated,
  EvaluateJobOut,
} from '../types';

// ---------------------------------------------------------------------------
// Checklists
// ---------------------------------------------------------------------------

export async function listChecklists(
  limit = 50,
  offset = 0,
): Promise<ApiResponse<ChecklistList>> {
  return checklistApi.get(`/v1/checklists?limit=${limit}&offset=${offset}`);
}

export async function createChecklist(body: {
  name: string;
  description?: string | null;
}): Promise<ApiResponse<ChecklistOut>> {
  return checklistApi.post('/v1/checklists', body);
}

export async function getChecklist(
  id: string,
): Promise<ApiResponse<ChecklistDetailOut>> {
  return checklistApi.get(`/v1/checklists/${id}`);
}

export async function deleteChecklist(checklistId: string): Promise<void> {
  return checklistApi.delete(`/v1/checklists/${checklistId}`);
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export async function listRules(
  limit = 50,
  offset = 0,
): Promise<ApiResponse<RuleList>> {
  return checklistApi.get(`/v1/rules?limit=${limit}&offset=${offset}`);
}

export async function createRule(body: {
  title: string;
  criteria: string;
  description?: string | null;
  condition?: string | null;
  section_match?: string | null;
  section_match_type?: 'exact' | 'regex' | 'document' | null;
}): Promise<ApiResponse<RuleOut>> {
  return checklistApi.post('/v1/rules', body);
}

export async function updateRule(
  ruleId: string,
  body: {
    title?: string;
    criteria?: string;
    description?: string | null;
    condition?: string | null;
    section_match?: string | null;
    section_match_type?: 'exact' | 'regex' | 'document' | null;
  },
): Promise<ApiResponse<RuleOut>> {
  return checklistApi.patch(`/v1/rules/${ruleId}`, body);
}

export async function addRuleToChecklist(
  checklistId: string,
  ruleId: string,
  position = 0,
): Promise<ApiResponse<RuleOut>> {
  return checklistApi.post(`/v1/checklists/${checklistId}/rules`, {
    rule_id: ruleId,
    position,
  });
}

export async function removeRuleFromChecklist(
  checklistId: string,
  ruleId: string,
): Promise<void> {
  return checklistApi.delete(`/v1/checklists/${checklistId}/rules/${ruleId}`);
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

export interface DocumentSection {
  title: string;
  content: string;
}

export interface EvaluateBody {
  checklist_id: string;
  content: {
    document?: string;
    sections?: DocumentSection[];
  };
  options?: { skip_na?: boolean };
}

export async function submitEvaluation(
  body: EvaluateBody,
): Promise<ApiResponse<EvaluateJobCreated>> {
  return checklistApi.post('/v1/evaluate', body);
}

export async function getEvaluation(
  jobId: string,
): Promise<ApiResponse<EvaluateJobOut>> {
  return checklistApi.get(`/v1/evaluate/${jobId}`);
}

// ---------------------------------------------------------------------------
// Polling helper
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2_500;

/** Poll until status is 'completed', 'failed', or 'cancelled'. No client timeout. */
export async function pollEvaluation(
  jobId: string,
  onPoll?: (data: EvaluateJobOut) => void,
): Promise<EvaluateJobOut> {
  for (;;) {
    const response = await getEvaluation(jobId);

    if (!response.data) {
      throw new Error(response.error?.message ?? 'Empty response from server');
    }

    const data = response.data;
    onPoll?.(data);

    if (data.status === 'completed') return data;
    if (data.status === 'failed') {
      throw new Error(data.error ?? 'Evaluation failed without error detail');
    }
    if (data.status === 'cancelled') {
      throw new Error('Evaluation was cancelled');
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
